import {
  AttrId,
  LoginInfo,
  Permission,
  Price,
  Rates,
  get_status,
  request_pw_reset,
} from "./api";
import { AttrDefinition } from "./bindings/AttrDefinition";
import { AttrVal } from "./bindings/AttrVal";
import { Currency } from "./bindings/Currency";

type ElemAttrs = {
  [key: string]: string;
};
export function elem(
  tag: string,
  attrs?: ElemAttrs | null,
  ...children: (ChildNode | string)[]
): HTMLElement {
  const e = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      e.setAttribute(key, value);
    }
  }
  for (const c of children) {
    if (typeof c == "string") {
      e.appendChild(document.createTextNode(c));
    } else {
      e.appendChild(c);
    }
  }
  return e;
}

export function make_form(items: [string, HTMLElement][]) {
  const div = elem("div", { class: "form" });
  for (const [label, item] of items) {
    const row = elem(
      "div",
      { class: "row" },
      elem("p", { class: "label" }, label),
      item,
    );
    div.appendChild(row);
  }
  return div;
}

export function button(
  name: string,
  handler: (this: HTMLElement, event: MouseEvent) => void,
  cls?: string,
): HTMLElement {
  const btn = elem("input", { type: "button", value: name, class: cls || "" });
  btn.addEventListener("click", handler);
  return btn;
}

export function login_prompt(
  callback: (user: string, password: string) => void,
): HTMLElement {
  const user = elem("input", {
    type: "text",
    autocomplete: "username",
  }) as HTMLInputElement;
  const pass = elem("input", {
    type: "password",
    autocomplete: "current-password",
  }) as HTMLInputElement;
  setTimeout(function () {
    if (user.value.length > 0 && pass.value.length > 0) {
      callback(user.value, pass.value);
    }
  }, 100);

  return elem(
    "div",
    { class: "login" },
    elem("div", null, elem("span", null, "Username: "), user),
    elem("div", null, elem("span", null, "Password: "), pass),
    button(
      "login",
      () => {
        callback(user.value, pass.value);
      },
      "main",
    ),
  );
}

export function input(type: "text" | "number" | "textarea"): HTMLInputElement {
  return elem("input", { type }) as HTMLInputElement;
}
export function icon_button(
  name: string,
  desc: string,
  on_click: () => void,
  extra_classes: string[] = [],
): HTMLElement {
  const e = elem(
    "div",
    { class: "icon" },
    elem("div", {
      class: ["icon-button", ...extra_classes].join(" "),
      "d-name": name,
      alt: desc,
    }),
  );
  e.addEventListener("click", (e) => {
    e.stopImmediatePropagation();
    on_click();
  });
  return e;
}

const lazy_price_formats = {};
function get_price_format(currency: Currency): Intl.NumberFormat {
  const f = lazy_price_formats[currency];
  if (f !== undefined) return f;

  const f2 = new Intl.NumberFormat(navigator.language, {
    currency,
    style: "currency",
  });
  lazy_price_formats[currency] = f2;
  return f2;
}
export function format_price(price: Price | null): string {
  if (price === null) {
    return "On request";
  }
  const { currency, val } = price;
  return get_price_format(currency as Currency)?.format(val);
}
export function format_size(size_cm: number[]): string {
  return size_cm.map((cm) => `${cm}cm`).join(" by ");
}
export function switch_class(
  elem: HTMLElement,
  class_name: string,
  on: boolean,
): void {
  if (on) {
    elem.classList.add(class_name);
  } else {
    elem.classList.remove(class_name);
  }
}
export function convert_price(
  rates: Rates,
  price: Price | null,
  to: Currency | null,
): Price {
  if (price === null) return null;
  if (to === null) return price;
  const eur =
    price.currency === "EUR" ? price.val : price.val / rates[price.currency];
  return { val: to === "EUR" ? eur : eur * rates[to], currency: to };
}

export const request_idle_callback =
  window.requestIdleCallback ||
  function (cb: IdleRequestCallback) {
    var start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1);
  };

export function randomUUID() {
  if ("randomUUID" in crypto && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const url = URL.createObjectURL(new Blob());
  const uuid = url.substring(url.length - 36);
  URL.revokeObjectURL(url);
  return uuid;
}

export function arraysEqual<T>(a: T[], b: T[]) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (var i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function debounce<T>(f: (arg: T) => Promise<void>): (arg: T) => void {
  let running = false;
  let last_arg: T | null = null;

  function complete() {
    if (last_arg !== null) {
      const arg = last_arg;
      last_arg = null;
      running = true;

      f(arg).then(complete, complete);
    } else {
      running = false;
      last_arg = null;
    }
  }

  return function (arg: T) {
    if (!running) {
      running = true;
      f(arg).then(complete, complete);
    } else {
      last_arg = arg;
    }
  };
}

interface ListSelectOptions<T> {
  on_select?(id: T): any;
  id?: string;
}
type ListSelect<T> = {
  node: HTMLSelectElement;
  value: () => T;
  set: (value: T) => void;
};
export function list_select<T extends string>(
  items: [T, string][],
  options: ListSelectOptions<T>,
): ListSelect<T> {
  const select = elem("select") as HTMLSelectElement;
  if (options.id !== undefined) {
    select.id = options.id;
  }

  for (const [id, name] of items) {
    select.appendChild(elem("option", { value: id }, name));
  }
  if (options.on_select !== undefined) {
    const s = options.on_select;
    select.addEventListener("change", (_) => s(select.value as T));
  }

  return {
    node: select,
    value: () => select.value as T,
    set: (val: T) => {
      select.value = val;
    },
  };
}

export function input_pass(): HTMLInputElement {
  return elem("input", { type: "password" }) as HTMLInputElement;
}

export function input_email(): HTMLInputElement {
  return elem("input", { type: "email" }) as HTMLInputElement;
}
export function div(...children: (HTMLElement | string)[]) {
  return elem("div", null, ...children);
}

export function option(
  name: string,
  init: boolean,
  callback: (v: boolean) => void,
) {
  const group = elem("div", { class: "option" });
  group.appendChild(elem("span", { class: "name" }, name));
  const input = elem("input", { type: "checkbox" }) as HTMLInputElement;
  input.checked = init;
  input.addEventListener("change", (_) => callback(input.checked));
  group.appendChild(input);
  return group;
}
export function non_empty(s: string): string | null {
  if (s.trim().length > 0) {
    return s;
  } else {
    return null;
  }
}

const sizes = [400, 600, 800, 1000, 2000];
export function select_size_from_width(css_pixels: number): number {
  const img_width = css_pixels;
  let size = sizes[0];
  let nearest = Infinity;
  for (const s of sizes) {
    const d = Math.abs(s - img_width);
    if (d < nearest) {
      size = s;
      nearest = d;
    }
  }
  return size;
}

// returns string containint [0-9], '.', ';'
export function stringify_attr_val(val: AttrVal): string {
  switch (val.type) {
    case "Length":
      return val.cm.toString();
    case "Volume":
      return val.liter.toString();
    case "Color":
      return val.cids.join(";");
    case "Weight":
      return val.kg.toString();
    case "Number":
      return val.n.toString();
    case "Enum":
      return val.idx.toString();
  }
}
export function parse_attr_string(def: AttrDefinition, s: string): AttrVal {
  switch (def.data.type) {
    case "Length":
      return { type: "Length", cm: parseFloat(s) };
    case "Volume":
      return { type: "Volume", liter: parseFloat(s) };
    case "Color":
      return {
        type: "Color",
        cids: s
          .split(";")
          .map((s) => parseInt(s))
          .filter(isFinite),
      };
    case "Weight":
      return { type: "Weight", kg: parseFloat(s) };
    case "Number":
      return { type: "Number", n: parseInt(s) };
    case "Enum":
      return {
        type: "Enum",
        idx: s
          .split(";")
          .map((s) => parseInt(s))
          .filter(isFinite),
      };
  }
}
export function parse_id_map<V>(obj: { [key in number]?: V }): Map<number, V> {
  if (obj === undefined) return;
  return new Map(Object.entries(obj).map(([k, v]) => [parseInt(k), v]));
}
export function map_to_object<K, V>(
  map: Map<K, V | null>,
): { [index: string]: V } {
  const entries: [K, V][] = Array.from(map.entries());
  return Object.fromEntries(entries.filter(([k, v]) => v !== null));
}
export function list_to_map<K, V>(list?: [K, V][]): Map<K, V> | undefined {
  if (list === undefined) return;
  return new Map(list);
}
export function assert(expr: unknown, msg?: string): asserts expr {
  if (!expr) throw new Error(msg);
}

export function area(names: Map<string, number>, attrs: Map<AttrId, AttrVal>) {
  if (names === undefined) return;
  const w_id = names.get("width");
  const l_id = names.get("length");
  if (w_id === undefined || l_id === undefined) return;

  const l = attrs.get(l_id);
  const w = attrs.get(w_id);
  console.log(w, l);
  if (l === undefined || w === undefined) return;

  if (l.type == "Length" && w.type == "Length") {
    return (w.cm * l.cm) / 10000;
  }
}

export function compareMap<K, V>(
  map_a: Map<K, V>,
  map_b: Map<K, V>,
  compare: (a: V, b: V) => boolean,
) {
  if (map_a.size !== map_b.size) return false;
  return map_a.entries().every(([k, a]) => compare(a, map_b.get(k)));
}
export function compareAttrVal(a: AttrVal, b: AttrVal): boolean {
  if (a.type !== b.type) return false;
  switch (a.type) {
    case "Length":
      assert(b.type === "Length");
      return a.cm === b.cm;

    case "Volume":
      assert(b.type === "Volume");
      return a.liter === b.liter;

    case "Color":
      assert(b.type === "Color");
      return compareArray(a.cids, b.cids, (a, b) => a === b);
    case "Weight":
      assert(b.type === "Weight");
      return a.kg === b.kg;

    case "Number":
      assert(b.type === "Number");
      return a.n === b.n;

    case "Enum":
      assert(b.type === "Enum");
      return compareArray(a.idx, b.idx, (a, b) => a === b);
  }
}
function compareArray<T>(
  a: T[],
  b: T[],
  compare: (a: T, b: T) => boolean,
): boolean {
  if (a.length !== b.length) return false;
  return a.every((a, i) => compare(a, b[i]));
}
export function filterMap<K, V>(
  data: Map<K, V>,
  filter: (pair: [K, V]) => boolean,
): Map<K, V> {
  const entries = Array.from(data.entries());
  return new Map(entries.filter(filter));
}
export function mapFilterMap<A, B, C, D>(
  data: Map<A, B>,
  map: (pair: [A, B]) => [C, D],
  filter: (pair: [C, D]) => boolean,
): Map<C, D> {
  const entries = Array.from(data.entries(), map);
  return new Map(entries.filter(filter));
}
export function mapMap<A, B, C, D>(
  data: Map<A, B>,
  map: (pair: [A, B]) => [C, D],
): Map<C, D> {
  const entries = Array.from(data.entries(), map);
  return new Map(entries);
}
export type CurrencyDef = { id: Currency; label: string };
export const currencies: CurrencyDef[] = [
  { id: "EUR", label: "Euro" },
  { id: "USD", label: "US Dollar" },
  { id: "MAD", label: "Moroccan Dirham" },
  { id: "CNY", label: "Chinese Yuan" },
  { id: "RUB", label: "Russian Rubel" },
];

const base64Alphabet =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
function _toBase64(array: Uint8Array): string {
  var result = "";
  var i = 0;
  var length = array.length;
  var triplet;

  function at(triplet, shift) {
    return base64Alphabet.charAt((triplet >> (6 * shift)) & 63);
  }

  for (; i + 2 < length; i += 3) {
    triplet = (array[i] << 16) + (array[i + 1] << 8) + array[i + 2];
    result += at(triplet, 3) + at(triplet, 2) + at(triplet, 1) + at(triplet, 0);
  }
  if (i + 2 === length) {
    triplet = (array[i] << 16) + (array[i + 1] << 8);
    result += at(triplet, 3) + at(triplet, 2) + at(triplet, 1) + "=";
  } else if (i + 1 === length) {
    triplet = array[i] << 16;
    result += at(triplet, 3) + at(triplet, 2) + "==";
  }

  return result;
}
function _toBase64_native(array: Uint8Array): string {
  return (array as any).toBase64();
}

const _toBase64_impl =
  "toBase64" in Uint8Array.prototype ? _toBase64_native : _toBase64;
export function toBase64(array: Uint8Array) {
  return _toBase64_impl(array);
}

export function cached<T>(func: () => T): () => T {
  let is_cached = false;
  let val;

  return () => {
    if (is_cached) return val;
    val = func();
    is_cached = true;
    return val;
  };
}

export function has_perm(info: LoginInfo, perm: string): boolean {
  return info?.permissions?.find(s => s === perm) !== undefined
}

const SIZE_PREFIX = ["", "K", "M", "G", "T", "P"]; // I hope nobody uploads a peta-byte sized file.
export function format_file_size(bytes: bigint): string {
    let base = 0;
    let n = Number(bytes);
    while (n > 1024) {
        n /= 1024;
        base += 1;
    }
    if (base === 0) {
        return `${n} B`;
    } else {
        return `${n.toPrecision(3)} ${SIZE_PREFIX[base]}B`;
    }
}
export function split_once(s: string, sep: string): [string, string] {
  const i = s.indexOf(sep);
  if (i === -1) {
    return [s, ""];
  }
  return [s.slice(0, i), s.slice(i + sep.length)];
}
